import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  IconButton,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ShareIcon from "@mui/icons-material/Share";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import PinterestIcon from "@mui/icons-material/Pinterest";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import img01 from "../../assets/landing/Nursery/img-01.jpg";
import img02 from "../../assets/landing/Nursery/img-02.jpg";
import img03 from "../../assets/landing/Nursery/img-03.jpg";
import img04 from "../../assets/landing/Nursery/img-04.jpg";
import img05 from "../../assets/landing/Nursery/img-05.jpg";
import img06 from "../../assets/landing/Nursery/img-06.jpg";
import img07 from "../../assets/landing/Nursery/img-07.jpg";
import img08 from "../../assets/landing/Nursery/img-08.jpg";
import img09 from "../../assets/landing/Nursery/img-09.jpg";

const NurserySection = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const nurseryImages = [
    img01,
    img02,
    img03,
    img04,
    img05,
    img06,
    img07,
    img08,
    img09,
  ];

  // Modal state
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomedIn, setZoomedIn] = useState(false);

  // Share menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const shareMenuOpen = Boolean(anchorEl);

  const handleOpen = (index) => {
    setCurrentIndex(index);
    setOpen(true);
    setZoomedIn(false);
  };

  const handleClose = () => {
    setOpen(false);
    setZoomedIn(false);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? nurseryImages.length - 1 : prev - 1
    );
    setZoomedIn(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === nurseryImages.length - 1 ? 0 : prev + 1
    );
    setZoomedIn(false);
  };

  const handleFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch((err) => {
        console.error(
          `Error attempting to enable fullscreen mode: ${err.message}`
        );
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Share menu handlers
  const handleShareClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setAnchorEl(null);
  };

  const handleShare = (type) => {
    handleShareClose();
    const imageUrl = nurseryImages[currentIndex];
    const pageUrl = window.location.href;

    switch (type) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            pageUrl
          )}`,
          "_blank"
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            pageUrl
          )}&text=Check%20out%20this%20image`,
          "_blank"
        );
        break;
      case "pinterest":
        window.open(
          `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
            pageUrl
          )}&media=${encodeURIComponent(imageUrl)}&description=Nursery%20Image`,
          "_blank"
        );
        break;
      case "download":
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = `nursery-image-${currentIndex + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        break;
      default:
        break;
    }
  };

  const renderGallery = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(3, 1fr)",
          sm: "repeat(3, 1fr)",
        },
        gap: 0.5,
        mb: 4,
      }}
    >
      {nurseryImages.map((img, index) => (
        <Box
          key={index}
          sx={{
            position: "relative",
            width: "100%",
            paddingBottom: "100%",
            overflow: "hidden",
            cursor: "pointer",
          }}
          onClick={() => handleOpen(index)}
        >
          <Box
            component="img"
            src={img}
            alt={`Nursery ${index + 1}`}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      width={{ xl: "70%", lg: "90%", md: "85%", sm: "90%", xs: "95%" }}
      sx={{
        py: 8,
        maxWidth: 1200,
        margin: "auto",
        borderBottom: "1px solid #31B44B",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          mb: 4,
        }}
      >
        <Box
          sx={{ flex: 1, borderBottom: "2px solid", borderColor: "#31B44B" }}
        />
        <Typography
          variant="h2"
          component="h1"
          className="welcome-title-text"
          sx={{
            fontSize: "2.5rem",
            mx: 2,
            whiteSpace: "nowrap",
            color: "#232323",
          }}
        >
          Our Nursery
        </Typography>
        <Box
          sx={{ flex: 1, borderBottom: "2px solid", borderColor: "#31B44B" }}
        />
      </Box>

      {isDesktop ? (
        <Box display="flex" gap={6}>
          <Box sx={{ width: "50%" }}>{renderGallery()}</Box>
          <Box
            sx={{ width: "40%", textAlign: "left" }}
            alignSelf="center"
            marginLeft="auto"
          >
            <Typography
              variant="h5"
              component="p"
              className="felipa-texts"
              sx={{ fontStyle: "italic", mb: 3 }}
            >
              Habitant morbi tristique senectus et. Faucibus turpis in eu mi
              bibendum.
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 5, lineHeight: 1.8, textAlign: "center" }}
            >
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
              enim ad minim veniam, quis nostrud exercitation ullamco laboris
              nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
              reprehenderit in voluptate velit esse cillum dolore eu fugiat
              nulla pariatur.
            </Typography>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                backgroundColor: "#31B44B",
                color: "white",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  backgroundColor: "#232323",
                  boxShadow: "0 6px 10px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              TAKE A TOUR <PlayArrowIcon />
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center" }}>
          {renderGallery()}
          <Typography
            variant="h5"
            component="p"
            sx={{ fontStyle: "italic", mb: 3, fontWeight: "bold" }}
          >
            Mafetauru won't trust me sincere at.
            <br />
            Faucibus turpis tu et mi bifendrum.
          </Typography>
          <Typography variant="body1" sx={{ mb: 5, lineHeight: 1.8 }}>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim
            ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
            aliquip ex ea commodo consequat. Duis aute irure dolor in
            reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla
            pariatur.
          </Typography>
          <Button
            variant="outlined"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              backgroundColor: "#31B44B",
              color: "white",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                backgroundColor: "#232323",
                boxShadow: "0 6px 10px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            TAKE A TOUR <PlayArrowIcon />
          </Button>
        </Box>
      )}

      {/* Modal */}
      <Modal open={open} onClose={handleClose} closeAfterTransition>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: theme.zIndex.modal,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            px: 2,
            pt: 2,
          }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              width: "95%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                color: "white",
                backgroundColor: "rgba(0,0,0,0.4)",
                borderRadius: "16px",
                px: 2,
                py: 0.5,
                fontSize: "14px",
              }}
            >
              {currentIndex + 1} / {nurseryImages.length}
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton sx={{ color: "white" }} onClick={handleFullscreen}>
                <FullscreenIcon />
              </IconButton>
              <IconButton
                sx={{ color: "white" }}
                onClick={() => setZoomedIn((z) => !z)}
              >
                <ZoomInIcon />
              </IconButton>
              <IconButton
                sx={{ color: "white" }}
                onClick={handleShareClick}
                aria-controls={shareMenuOpen ? "share-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={shareMenuOpen ? "true" : undefined}
              >
                <ShareIcon />
              </IconButton>
              <Menu
                id="share-menu"
                anchorEl={anchorEl}
                open={shareMenuOpen}
                onClose={handleShareClose}
                MenuListProps={{
                  "aria-labelledby": "share-button",
                }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                sx={{
                  "& .MuiPaper-root": {
                    backgroundColor: "white",
                    color: "black",
                  },
                  marginTop: "50px",
                }}
              >
                <MenuItem onClick={() => handleShare("facebook")}>
                  <ListItemIcon>
                    <FacebookIcon fontSize="small" sx={{ color: "#3b5998" }} />
                  </ListItemIcon>
                  Share on Facebook
                </MenuItem>
                <MenuItem onClick={() => handleShare("twitter")}>
                  <ListItemIcon>
                    <TwitterIcon fontSize="small" sx={{ color: "#1da1f2" }} />
                  </ListItemIcon>
                  Share on Twitter
                </MenuItem>
                <MenuItem onClick={() => handleShare("pinterest")}>
                  <ListItemIcon>
                    <PinterestIcon fontSize="small" sx={{ color: "#bd081c" }} />
                  </ListItemIcon>
                  Pin it
                </MenuItem>
                <Divider sx={{ bgcolor: "#555", my: 0.5 }} />
                <MenuItem onClick={() => handleShare("download")}>
                  <ListItemIcon>
                    <FileDownloadIcon fontSize="small" sx={{ color: "grey" }} />
                  </ListItemIcon>
                  Download image
                </MenuItem>
              </Menu>
              <IconButton onClick={handleClose} sx={{ color: "white" }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Image with arrows */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "90vw",
              maxHeight: "80vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={handlePrev}
              sx={{
                position: "absolute",
                left: 0,
                color: "white",
                backgroundColor: "rgba(0,0,0,0.5)",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.7)",
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            <Box
              component="img"
              src={nurseryImages[currentIndex]}
              alt={`Nursery ${currentIndex + 1}`}
              sx={{
                width: "auto",
                maxHeight: "80vh",
                maxWidth: "100%",
                borderRadius: 2,
                transition: "transform 0.3s ease",
                transform: zoomedIn ? "scale(1.5)" : "scale(1)",
                cursor: zoomedIn ? "zoom-out" : "zoom-in",
              }}
              onClick={() => setZoomedIn((z) => !z)}
            />

            <IconButton
              onClick={handleNext}
              sx={{
                position: "absolute",
                right: 0,
                color: "white",
                backgroundColor: "rgba(0,0,0,0.5)",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.7)",
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default NurserySection;
